<template>
  <div class="app-container">
    <Header :show-scroll-progress="true" />
    <div class="blog-content-container">
      <div :class="mainContentClass">
        <BlogHeader
          :title="title"
          :subheading="subheading"
          :date="date"
          :author="author"
          :author-avatar="authorAvatar"
          :reading-time="readingTime"
          :hero-image="heroImage"
        />

        <div class="gigs-mrr-blog">
          <section class="blog-section">
            <p>
              Every drone operator eventually feels the same pressure: the month
              looks full, then suddenly three clients delay projects at the same
              time and the calendar collapses. Feast one month, famine the next.
              It's not because the work isn't valuable. It's because the work is
              sold as isolated gigs rather than ongoing responsibility. And
              responsibility is what clients actually want from you. They want
              consistency, coverage, and someone who remembers the context of
              their assets better than they do.
            </p>
            <p>
              Live-access retainers are one of those ideas that sound
              complicated until you try them once. Then the simplicity becomes
              obvious. Instead of selling "a flight," you are selling
              availability. You are selling a predictable number of live
              sessions per month. You are selling monitoring, recording, and
              being able to respond faster than any ad-hoc operator ever could.
              When clients understand that you're not just a pair of hands with
              a drone but a recurring source of visibility, they stop thinking
              in terms of one-off jobs. They start thinking in terms of
              partnership.
            </p>
            <p>
              What makes retainers particularly powerful for DSPs is that they
              align perfectly with how most industrial, construction, and
              utility clients already operate. These clients do not wake up one
              morning and suddenly need zero drone work. Their needs are
              cyclical. Their assets degrade, grow, shift, and need to be
              monitored. They have recurring safety checks. They have monthly
              executive updates. They have ongoing progress documentation. A
              retainer gives them the structure and reliability they need
              without having to renegotiate every time.
            </p>
          </section>

          <section class="blog-section">
            <h2>Where most operators overthink it</h2>
            <p>
              You do not need to invent a complicated pricing matrix. Start
              simple. A Good tier that includes a few live sessions per month. A
              Better tier with more sessions and priority timing. A Best tier
              that covers heavy usage, faster turnaround, and additional
              analytics or reports. These kinds of packages feel natural to
              clients because they map to how they already buy other services:
              IT support, consulting hours, field inspections, and so on.
            </p>
            <p>
              And the reality is that usage patterns stabilize very quickly. If
              a client consistently needs six sessions per month in construction
              phase, they rarely drop to zero later. Instead, they shift to
              fewer but more strategic flights: periodic health checks,
              compliance verification, or visual summaries for meetings. The
              brilliance of the retainer model is that it grows and shrinks with
              the client's pace without forcing you into constant quoting.
            </p>
          </section>

          <section class="blog-section">
            <h2>The math that helps you sleep at night</h2>
            <p>
              Every DSP who switches to retainers experiences the same relief.
              Suddenly the month no longer depends entirely on whether three new
              jobs materialize. You have a baseline. Your crew hours are
              predictable. Your equipment planning becomes rational. You can
              invest in better hardware or training because the revenue isn't
              bouncing around wildly.
            </p>
            <p>
              And here's the surprising part: your effective hourly margin
              almost always goes up. When you know how many sessions you will
              run in a month, you plan better. You avoid wasted travel. You
              batch tasks. You slot clients intelligently. The difference
              between guessing your workload and knowing it is enormous.
            </p>
          </section>

          <section class="blog-section">
            <h2>What clients actually appreciate</h2>
            <p>
              Clients love structure more than discounts. They love knowing that
              they have five sessions banked. They love receiving a monthly
              recap email that summarizes what you inspected, what was captured,
              and what needs attention. These small touches reduce churn because
              they prove ongoing value. Many operators find that a simple
              automation — a recap email with links to the month's recordings
              and any notes — keeps clients engaged without any heavy lifting.
            </p>
            <p>
              Some clients will even ask for upgrades once they realize that
              your team can handle analytics, change detection, or a guaranteed
              response time. That is when a 48-hour SLA becomes a profitable
              add-on instead of a burden.
            </p>
          </section>

          <section class="blog-section">
            <h2>How Skyhost helps a retainer feel like a real product</h2>
            <p>
              Live-access retainers work best when the experience is smooth.
              Scheduled sessions. A single link that always works. Usage
              tracking so clients can see how many sessions they've consumed.
              Auto-generated logs that flow nicely into end-of-month summaries.
              Overages that feel fair because they are clearly communicated.
            </p>
            <p>
              Skyhost gives you these structural pieces so the retainer feels
              like a service, not an informal arrangement. Clients can join
              sessions easily. You can track minutes or flights without
              spreadsheets. And reporting becomes almost automatic since the
              system already captures most of what matters.
            </p>
          </section>

          <section class="blog-section" style="border-bottom: none">
            <h2>The moment you shift from operator to partner</h2>
            <p>
              The deeper change happens inside your business. When clients start
              paying you every month, you behave differently. You think
              long-term. You make better decisions about gear, staffing, and
              scheduling. You stop chasing every small job because your
              foundation is stable. And clients notice that stability. It
              reassures them.
            </p>
            <p>
              The best part is that retainers are not theoretical. They are
              happening already. Some DSPs see 15 to 35 percent of their revenue
              come from recurring packages within a year of adopting them.
              Utilization improves because sessions spread out more
              consistently. And the operator's stress levels drop dramatically.
            </p>
            <p>
              If you have never offered a retainer before, test it with one
              client who already trusts you. Offer them a simple package: a few
              live sessions per month plus recordings and a short recap. See how
              naturally the workflow fits. Most operators are surprised by how
              quickly clients say yes.
            </p>
            <p>
              Ready to transform your operations? Get started with
              <a href="https://skyhost.ai" target="_blank" class="blog-link"
                >Skyhost</a
              >
              today.
            </p>
          </section>
        </div>

        <BlogFooter
          :tags="tags"
          :title="title"
          :current-page-url="currentPageUrl"
          :related-blogs="relatedBlogs"
          @navigate-to-blog="navigateToBlog"
          @navigate-to-blogs="navigateToBlogs"
        />
      </div>
    </div>

    <Footer />
  </div>
</template>

<script>
import { isMobile } from "@/utils/helper";
import { computed } from "vue";
import Header from "@/components/organisms/Header.vue";
import Footer from "@/components/Footer.vue";
import BlogHeader from "@/components/organisms/BlogHeader.vue";
import BlogFooter from "@/components/organisms/BlogFooter.vue";
import { blogsRegistry } from "@/data/blogRegistry";

export default {
  name: "GigsToMrrRetainersBlog",
  components: {
    Header,
    Footer,
    BlogHeader,
    BlogFooter,
  },
  data() {
    const blogId = "gigs-to-mrr-retainers";
    const blog = blogsRegistry[blogId];

    return {
      id: blogId,
      title: blog.title,
      subheading: blog.subheading,
      date: blog.date,
      author: blog.author,
      authorAvatar: blog.authorAvatar,
      image: blog.image,
      heroImage: blog.image,
      excerpt: blog.excerpt,
      tags: blog.tags,
      relatedBlogs: [],
    };
  },
  computed: {
    isMobileLayout() {
      return isMobile();
    },
    currentPageUrl() {
      return `${window.location.origin}${this.$route.fullPath}`;
    },
    readingTime() {
      const wordCount =
        this.title.split(" ").length +
        (this.excerpt?.split(" ").length || 0) +
        2100;
      return Math.ceil(wordCount / 200);
    },
  },
  methods: {
    navigateToBlog(blogId) {
      this.$router.push({
        path: `/blog/${blogId}`,
      });
    },
    navigateToBlogs() {
      this.$router.push({
        path: "/blogs",
      });
    },
  },
  created() {
    // Get related blogs (excluding current blog)
    const allOtherBlogs = Object.values(blogsRegistry).filter(
      (blog) => blog.id !== this.id
    );

    // First, try to get blogs with matching tags
    const matchingTagBlogs = allOtherBlogs.filter((blog) =>
      blog.tags.some((tag) => this.tags.includes(tag))
    );

    // If we have matching tag blogs, use them, otherwise use any other blogs
    const relatedBlogs =
      matchingTagBlogs.length > 0 ? matchingTagBlogs : allOtherBlogs;

    // Take up to 2 blogs
    this.relatedBlogs = relatedBlogs.slice(0, 2);
  },
  setup() {
    const mainContentClass = computed(() => {
      return isMobile() ? "mobile-blog-content" : "web-blog-content";
    });
    return {
      mainContentClass,
    };
  },
};
</script>

<style scoped>
.app-container {
  display: flex;
  flex-direction: column;
  min-height: 100vh;
  background-color: #fff;
  color: #333;
}

.blog-content-container {
  margin-top: 5rem;
}

.web-blog-content {
  max-width: 800px;
  margin: 0 auto;
  padding: 2rem;
}

.mobile-blog-content {
  padding: 1.5rem;
}
.gigs-mrr-blog {
  font-family: "Charter", "Georgia", "Cambria", serif;
  line-height: 1.8;
  color: #333;
  font-size: 21px;
}

.blog-section {
  margin-bottom: 4rem;
  padding: 2rem 0;
  border-bottom: 1px solid #f0f0f0;
}

h2 {
  font-family: "Inter", -apple-system, BlinkMacSystemFont, "Segoe UI",
    sans-serif;
  font-size: 2rem;
  margin-bottom: 1.5rem;
  color: #000;
  font-weight: 700;
  letter-spacing: -0.01em;
  line-height: 1.2;
}

h3 {
  font-family: "Inter", -apple-system, BlinkMacSystemFont, "Segoe UI",
    sans-serif;
  font-size: 1.4rem;
  margin-bottom: 1rem;
  color: #000;
  font-weight: 600;
  letter-spacing: -0.01em;
}

p {
  margin-bottom: 1.5rem;
  font-size: 21px;
  line-height: 1.8;
  font-family: "Charter", "Georgia", "Cambria", serif;
}

.blog-link {
  color: #1565c0;
  text-decoration: underline;
}

@media (max-width: 768px) {
  .web-blog-content {
    padding: 1.5rem;
  }

  h2 {
    font-size: 1.75rem;
  }
}
</style>
